import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
    LayoutGrid, List, Columns3, Search, SlidersHorizontal, X,
    ArrowUpDown, Plus, Pin, Bot, Sparkles, User, Hash, Check, NotebookPen,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Surface } from '../layout/Surface';
import { cn } from '../../lib/utils';
import { fileSource, isChatUpload, isDraft, isDraftAttachment, lowerContent } from './fileMeta';

/**
 * FileControlBar — the instrument strip above the artifact list.
 *
 * Replaces the old "Files · 24 artifacts · 3 pinned" page header, which made
 * this the last titled route in the app: the nav rail already says Artifacts,
 * and the grid underneath already shows how many there are. Same trade as
 * TaskControlBar — roughly half the height, and it does something.
 *
 * Two Selects and a bespoke "N chat" toggle came off the bar and into the
 * filters popover, which is what buys room for search and sort without the bar
 * growing. Active filters spill back out as chips below, because a filter you
 * can't see is a filter that makes files vanish for no visible reason.
 */

export const VIEWS = [
    { id: 'grid', icon: LayoutGrid, label: 'Grid' },
    { id: 'list', icon: List, label: 'List' },
    { id: 'shelf', icon: Columns3, label: 'Shelf' },
];

export const SORTS = [
    { id: 'edited', label: 'Edited' },
    { id: 'created', label: 'Newest' },
    { id: 'name', label: 'Name' },
    { id: 'type', label: 'Type' },
];

export const SOURCES = [
    { id: 'you', label: 'You', icon: User },
    { id: 'advisor', label: 'Advisor', icon: Sparkles },
];

export const EMPTY_FILTERS = {
    categories: [],
    tags: [],
    source: [],
    pinnedOnly: false,
    // Files you dropped into the chat composer. Hidden by default — they're
    // conversation attachments, not project artifacts — but the bar always says
    // so, which the old silent `showChatFiles` flag never did.
    showChatUploads: false,
    // Scratchpad notes. Same trade as chat uploads: hidden by default because a
    // draft is a thought you had once, surfaced on demand because a draft you
    // can't find is a draft you lost.
    showDrafts: false,
};

// Pinned floats to the top of every sort: it's a user's explicit "keep this
// where I can see it", and a sort order that buries it makes pinning useless.
const byPinned = (a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
const time = (v) => (v ? new Date(v).getTime() : 0);

const COMPARATORS = {
    edited: (a, b) => byPinned(a, b) || time(b.last_edited) - time(a.last_edited),
    created: (a, b) => byPinned(a, b) || time(b.created_at) - time(a.created_at),
    name: (a, b) => byPinned(a, b) || a.name.localeCompare(b.name),
    type: (a, b) => byPinned(a, b) || (a.type || '').localeCompare(b.type || '') || a.name.localeCompare(b.name),
};

/**
 * Pure: one predicate feeds the grid, the list and the shelf, so a file hidden
 * in one view is hidden in all of them.
 *
 * One pass rather than the four chained `.filter()`s plus a `.sort()` this
 * replaces — it runs against every file's full body on every keystroke, so the
 * cheap tests go first and the content scan goes last.
 */
export function filterAndSortFiles(files, query, filters, sortId) {
    const q = query.trim().toLowerCase();
    const { categories, tags, source, pinnedOnly, showChatUploads, showDrafts } = filters;

    const out = [];
    for (const f of files) {
        // No toggle reveals these: an image pasted into a draft is a dependency
        // of that draft, not a file the user ever asked to have.
        if (isDraftAttachment(f)) continue;
        if (!showDrafts && isDraft(f)) continue;
        if (!showChatUploads && isChatUpload(f)) continue;
        if (pinnedOnly && !f.pinned) continue;
        if (categories.length && !categories.includes(f.category)) continue;
        if (source.length && !source.includes(fileSource(f))) continue;
        if (tags.length && !tags.some((t) => (f.tags || []).includes(t))) continue;
        if (q && !f.name.toLowerCase().includes(q) && !lowerContent(f).includes(q)) continue;
        out.push(f);
    }
    // `out` is ours, so sorting in place is safe.
    return out.sort(COMPARATORS[sortId] || COMPARATORS.edited);
}

export default function FileControlBar({
    activeView,
    onViewChange,
    query,
    onQueryChange,
    filters,
    onFiltersChange,
    sort,
    onSortChange,
    tags,
    categories,
    chatUploadCount,
    draftCount,
    shown,
    total,
    readOnly,
    onNew,
    bulkSlot,
}) {
    const searchRef = useRef(null);
    const [searchExpanded, setSearchExpanded] = useState(false);
    const activeIndex = VIEWS.findIndex((v) => v.id === activeView);
    const activeSort = SORTS.find((s) => s.id === sort) || SORTS[0];

    // ── The sliding indicator ────────────────────────────────────────────────
    // Measured off the button it is highlighting, not computed as 1/n of the
    // track. The segments are content-sized and their labels vanish below the
    // `@[42rem]/filebar` container tier, so percentage math drifts.
    const trackRef = useRef(null);
    const tabRefs = useRef([]);
    const [indicator, setIndicator] = useState(null);

    useLayoutEffect(() => {
        const tabs = tabRefs.current;
        tabs.length = VIEWS.length;

        const measure = () => {
            const el = tabs[activeIndex];
            if (el) setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
        };

        measure();

        const ro = new ResizeObserver(measure);
        if (trackRef.current) ro.observe(trackRef.current);
        for (const tab of tabs) if (tab) ro.observe(tab);
        return () => ro.disconnect();
    }, [activeIndex, searchExpanded]);

    const toggle = (key, value) => {
        const current = filters[key] ?? [];
        onFiltersChange({
            ...filters,
            [key]: current.includes(value)
                ? current.filter((v) => v !== value)
                : [...current, value],
        });
    };

    const set = (key, value) => onFiltersChange({ ...filters, [key]: value });

    const chips = useMemo(() => {
        const out = [];
        for (const id of filters.categories) {
            const cat = categories.find((c) => c.id === id);
            out.push({ key: `cat-${id}`, label: cat?.label || id, onRemove: () => toggle('categories', id) });
        }
        for (const t of filters.tags) {
            out.push({ key: `tag-${t}`, label: `#${t}`, onRemove: () => toggle('tags', t) });
        }
        for (const s of filters.source) {
            const meta = SOURCES.find((x) => x.id === s);
            out.push({ key: `src-${s}`, label: `By ${meta?.label || s}`, onRemove: () => toggle('source', s) });
        }
        if (filters.pinnedOnly) {
            out.push({ key: 'pinned', label: 'Pinned', onRemove: () => set('pinnedOnly', false) });
        }
        return out;
        // eslint-disable-next-line
    }, [filters, categories]);

    // `/` focuses search, the way every list-heavy tool does it. Guarded on the
    // active element so it stays a slash when you're renaming a file.
    useEffect(() => {
        const handleKey = (e) => {
            if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
            const el = document.activeElement;
            if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
            e.preventDefault();
            setSearchExpanded(true);
            requestAnimationFrame(() => searchRef.current?.focus());
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, []);

    const isSearchOpen = searchExpanded || query.trim().length > 0;

    const hiddenUploads = !filters.showChatUploads && chatUploadCount > 0;
    const hiddenDrafts = !filters.showDrafts && draftCount > 0;
    // The second row appears whenever anything is narrowing the list — including
    // a bare search, where the count is the only signal the grid is partial, and
    // hidden chat uploads, which used to vanish files with no indication at all.
    const isNarrowed = chips.length > 0 || query.trim().length > 0 || hiddenUploads || hiddenDrafts;

    const filterCount = chips.length;

    const categoryToggles = categories.map((cat) => {
        const on = filters.categories.includes(cat.id);
        return (
            <button
                key={cat.id}
                onClick={() => toggle('categories', cat.id)}
                aria-pressed={on}
                className={cn(
                    'flex-shrink-0 whitespace-nowrap rounded-lg border px-2 py-1 text-[10px] font-bold uppercase tracking-wide transition-all',
                    on
                        ? 'border-primary/40 bg-primary/15 text-primary'
                        : 'border-white/10 text-muted-foreground hover:border-white/20 hover:text-foreground'
                )}
            >
                {cat.label}
            </button>
        );
    });

    return (
        <Surface as="header" variant="bar" aria-label="Artifact controls" className="@container/filebar flex-shrink-0">
            <div className="flex items-center gap-2 px-2 py-2 @[36rem]/filebar:gap-2.5 md:px-3">
                {/* View switcher — sliding pill */}
                <div
                    ref={trackRef}
                    className={cn(
                        'relative flex-shrink-0 items-center gap-1 rounded-2xl bg-secondary/30 p-1',
                        isSearchOpen ? 'hidden @[32rem]/filebar:flex' : 'flex'
                    )}
                >
                    <div
                        aria-hidden
                        className={cn(
                            'absolute top-1 bottom-1 rounded-xl bg-primary shadow-lg transition-all duration-300 ease-out',
                            !indicator && 'opacity-0'
                        )}
                        style={{ left: indicator?.left ?? 0, width: indicator?.width ?? 0 }}
                    />
                    {VIEWS.map((view, i) => {
                        const ViewIcon = view.icon;
                        const isActive = activeView === view.id;
                        return (
                            <button
                                key={view.id}
                                ref={(el) => { tabRefs.current[i] = el; }}
                                onClick={() => onViewChange(view.id)}
                                aria-pressed={isActive}
                                data-testid={`files-view-${view.id}`}
                                className={cn(
                                    'relative z-10 flex items-center justify-center gap-2 rounded-xl px-3 py-1.5 text-sm font-medium transition-colors duration-200 @[42rem]/filebar:px-3.5',
                                    isActive ? 'text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                                )}
                            >
                                <ViewIcon className={cn('h-4 w-4 transition-transform duration-200', isActive && 'scale-110')} />
                                <span className="hidden @[42rem]/filebar:inline">{view.label}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Compact search trigger — shown below @[32rem]/filebar when search is collapsed */}
                {!isSearchOpen && (
                    <button
                        type="button"
                        onClick={() => {
                            setSearchExpanded(true);
                            requestAnimationFrame(() => searchRef.current?.focus());
                        }}
                        aria-label="Search artifacts"
                        data-testid="files-search-trigger"
                        className="ml-auto flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-muted-foreground transition-colors hover:border-white/20 hover:text-foreground @[32rem]/filebar:hidden"
                    >
                        <Search className="h-4 w-4" />
                    </button>
                )}

                {/* Search. Capped rather than left to fill — on a wide monitor an
                    unbounded input stretches to ~1000px and the bar reads as one
                    giant text field with controls stuck to the ends. On compact
                    widths (< @[32rem]/filebar), expands to fill the whole bar when
                    activated and hides the sibling controls. */}
                <div
                    className={cn(
                        'relative min-w-0 flex-1 @[32rem]/filebar:max-w-xs @[48rem]/filebar:max-w-sm',
                        isSearchOpen ? 'block' : 'hidden @[32rem]/filebar:block'
                    )}
                >
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <input
                        ref={searchRef}
                        type="text"
                        value={query}
                        onChange={(e) => onQueryChange(e.target.value)}
                        onBlur={() => {
                            if (!query.trim()) setSearchExpanded(false);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                                onQueryChange('');
                                setSearchExpanded(false);
                                e.currentTarget.blur();
                            }
                        }}
                        placeholder="Search names and contents…"
                        aria-label="Search artifacts"
                        data-testid="files-search"
                        className="h-9 w-full rounded-xl border border-white/10 bg-white/[0.03] pl-9 pr-8 text-sm outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-primary/40 focus:bg-white/[0.05]"
                    />
                    {(query || searchExpanded) && (
                        <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                                onQueryChange('');
                                setSearchExpanded(false);
                                searchRef.current?.blur();
                            }}
                            aria-label="Clear search"
                            data-testid="files-search-clear"
                            className={cn(
                                'absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground',
                                !query && '@[32rem]/filebar:hidden'
                            )}
                        >
                            <X className="h-3 w-3" />
                        </button>
                    )}
                </div>

                {/* Category filter, promoted onto the bar itself — it's the one
                    filter worth a click rather than three. Hidden below @[62rem]/filebar
                    where custom categories would crowd the search; still in the
                    popover at every width. */}
                <div
                    className="ml-auto hidden min-w-0 flex-shrink items-center gap-1 overflow-x-auto no-scrollbar @[62rem]/filebar:flex"
                    role="group"
                    aria-label="Filter by category"
                >
                    {categoryToggles}
                </div>

                {/* Right-hand controls cluster */}
                <div
                    className={cn(
                        'items-center gap-1.5 @[32rem]/filebar:ml-auto @[62rem]/filebar:ml-0',
                        isSearchOpen ? 'hidden @[32rem]/filebar:flex' : 'flex'
                    )}
                >
                    {/* Sort */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                data-testid="files-sort"
                                aria-label={`Sort by ${activeSort.label}`}
                                className="flex h-9 flex-shrink-0 items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-2.5 text-sm text-muted-foreground transition-colors hover:border-white/20 hover:text-foreground @[42rem]/filebar:px-3"
                            >
                                <ArrowUpDown className="h-3.5 w-3.5" />
                                <span className="hidden @[42rem]/filebar:inline">{activeSort.label}</span>
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                            {SORTS.map((s) => (
                                <DropdownMenuItem
                                    key={s.id}
                                    onSelect={() => onSortChange(s.id)}
                                    className="gap-2 text-xs"
                                >
                                    <Check className={cn('h-3.5 w-3.5', s.id !== sort && 'opacity-0')} />
                                    {s.label}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    {/* Filters */}
                    <Popover>
                        <PopoverTrigger asChild>
                            <button
                                data-testid="files-filters"
                                aria-label="Filters"
                                className={cn(
                                    'flex h-9 flex-shrink-0 items-center gap-2 rounded-xl border px-2.5 text-sm transition-colors @[42rem]/filebar:px-3',
                                    filterCount
                                        ? 'border-primary/40 bg-primary/15 text-primary'
                                        : 'border-white/10 bg-white/[0.03] text-muted-foreground hover:border-white/20 hover:text-foreground'
                                )}
                            >
                                <SlidersHorizontal className="h-3.5 w-3.5" />
                                <span className="hidden @[42rem]/filebar:inline">Filters</span>
                                {filterCount > 0 && (
                                    <span className="rounded-full bg-primary/25 px-1.5 text-[10px] font-bold tabular-nums">
                                        {filterCount}
                                    </span>
                                )}
                            </button>
                        </PopoverTrigger>
                        {/* Radix portals to <body>, so the surface's overflow-hidden
                            can't clip this — and container queries don't reach here,
                            so Category is always available inside the popover. */}
                        <PopoverContent
                            align="end"
                            sideOffset={8}
                            className="max-h-[70vh] w-64 overflow-y-auto rounded-2xl border-white/10 bg-[hsl(240_11%_7%)]/95 p-3 backdrop-blur-xl"
                        >
                            <div className="mb-3">
                                <p className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                                    Category
                                </p>
                                <div className="flex flex-wrap gap-1.5">{categoryToggles}</div>
                            </div>

                            <div className="mb-3">
                                <p className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                                    Source
                                </p>
                                <div className="flex gap-1.5">
                                    {SOURCES.map((s) => {
                                        const on = filters.source.includes(s.id);
                                        const SrcIcon = s.icon;
                                        return (
                                            <button
                                                key={s.id}
                                                onClick={() => toggle('source', s.id)}
                                                aria-pressed={on}
                                                className={cn(
                                                    'flex flex-1 items-center justify-center gap-1 rounded-lg border px-2 py-1.5 text-[10px] font-bold uppercase transition-all',
                                                    on
                                                        ? 'border-primary/40 bg-primary/15 text-primary'
                                                        : 'border-white/10 text-muted-foreground hover:border-white/20 hover:text-foreground'
                                                )}
                                            >
                                                <SrcIcon className="h-3 w-3" />
                                                {s.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {tags.length > 0 && (
                                <div className="mb-3">
                                    <p className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                                        Tags
                                    </p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {tags.map((t) => {
                                            const on = filters.tags.includes(t);
                                            return (
                                                <button
                                                    key={t}
                                                    onClick={() => toggle('tags', t)}
                                                    aria-pressed={on}
                                                    className={cn(
                                                        'inline-flex items-center gap-0.5 rounded-lg border px-2 py-1 text-[10px] font-medium transition-all',
                                                        on
                                                            ? 'border-primary/40 bg-primary/15 text-primary'
                                                            : 'border-white/10 text-muted-foreground hover:border-white/20 hover:text-foreground'
                                                    )}
                                                >
                                                    <Hash className="h-2.5 w-2.5" />
                                                    {t}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            <div className="space-y-1.5 border-t border-white/[0.06] pt-3">
                                <PopoverSwitch
                                    icon={Pin}
                                    label="Pinned only"
                                    on={filters.pinnedOnly}
                                    onToggle={() => set('pinnedOnly', !filters.pinnedOnly)}
                                />
                                {draftCount > 0 && (
                                    <PopoverSwitch
                                        icon={NotebookPen}
                                        label={`Show drafts (${draftCount})`}
                                        on={filters.showDrafts}
                                        onToggle={() => set('showDrafts', !filters.showDrafts)}
                                        testId="files-toggle-drafts"
                                    />
                                )}
                                {chatUploadCount > 0 && (
                                    <PopoverSwitch
                                        icon={Bot}
                                        label={`Show chat uploads (${chatUploadCount})`}
                                        on={filters.showChatUploads}
                                        onToggle={() => set('showChatUploads', !filters.showChatUploads)}
                                        testId="files-toggle-chat-uploads"
                                    />
                                )}
                            </div>
                        </PopoverContent>
                    </Popover>

                    {!readOnly && (
                        <button
                            onClick={onNew}
                            data-testid="files-new"
                            aria-label="New artifact"
                            className="flex h-9 flex-shrink-0 items-center gap-2 rounded-xl bg-primary px-2.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 @[42rem]/filebar:px-3"
                        >
                            <Plus className="h-4 w-4" />
                            <span className="hidden @[42rem]/filebar:inline">New</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Active filters — the visible half of the popover trade, and where
                bulk actions live. */}
            {isNarrowed && (
                <div className="flex flex-wrap items-center gap-1.5 border-t border-white/[0.06] px-2 py-2 md:px-3">
                    {chips.map((chip) => (
                        <button
                            key={chip.key}
                            onClick={chip.onRemove}
                            title="Remove filter"
                            className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase text-primary transition-opacity hover:opacity-75"
                        >
                            {chip.label}
                            <X className="h-2.5 w-2.5" />
                        </button>
                    ))}

                    {/* Not a filter you set, so it doesn't get the primary chip —
                        but it is hiding files, so it has to be visible and it has
                        to be one click to undo. */}
                    {hiddenDrafts && (
                        <button
                            onClick={() => set('showDrafts', true)}
                            title="Show drafts"
                            data-testid="files-drafts-hidden"
                            className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/5 px-2.5 py-0.5 text-[10px] font-bold uppercase text-muted-foreground transition-colors hover:border-white/25 hover:text-foreground"
                        >
                            <NotebookPen className="h-2.5 w-2.5" />
                            {draftCount} {draftCount === 1 ? 'draft' : 'drafts'} hidden
                        </button>
                    )}

                    {hiddenUploads && (
                        <button
                            onClick={() => set('showChatUploads', true)}
                            title="Show chat uploads"
                            className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/5 px-2.5 py-0.5 text-[10px] font-bold uppercase text-muted-foreground transition-colors hover:border-white/25 hover:text-foreground"
                        >
                            <Bot className="h-2.5 w-2.5" />
                            {chatUploadCount} chat hidden
                        </button>
                    )}

                    {chips.length > 0 && (
                        <button
                            onClick={() => onFiltersChange({
                                ...EMPTY_FILTERS,
                                // "Clear" undoes the filters you set. The two
                                // reveal toggles aren't filters — clearing them
                                // would hide files you had just asked to see.
                                showChatUploads: filters.showChatUploads,
                                showDrafts: filters.showDrafts,
                            })}
                            className="ml-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground"
                        >
                            Clear
                        </button>
                    )}

                    <div className="ml-auto flex items-center gap-2">
                        <span className="text-[11px] tabular-nums text-muted-foreground">
                            {shown} of {total}
                        </span>
                        {!readOnly && bulkSlot}
                    </div>
                </div>
            )}

            {/* Bulk actions need a home even with nothing filtered — a selection
                is its own kind of narrowing. */}
            {!isNarrowed && !readOnly && bulkSlot && (
                <div className="flex items-center gap-2 border-t border-white/[0.06] px-2 py-2 md:px-3">
                    <span className="text-[11px] tabular-nums text-muted-foreground">{total} artifacts</span>
                    <div className="ml-auto">{bulkSlot}</div>
                </div>
            )}
        </Surface>
    );
}

function PopoverSwitch({ icon: Icon, label, on, onToggle, testId }) {
    return (
        <button
            onClick={onToggle}
            aria-pressed={on}
            data-testid={testId}
            className={cn(
                'flex w-full items-center gap-2 rounded-lg border px-2 py-1.5 text-[11px] font-medium transition-all',
                on
                    ? 'border-primary/40 bg-primary/15 text-primary'
                    : 'border-white/10 text-muted-foreground hover:border-white/20 hover:text-foreground'
            )}
        >
            <Icon className="h-3 w-3 flex-shrink-0" />
            <span className="truncate text-left">{label}</span>
            <span
                className={cn(
                    'ml-auto h-3 w-3 flex-shrink-0 rounded-full border transition-colors',
                    on ? 'border-primary bg-primary' : 'border-white/20'
                )}
            />
        </button>
    );
}
